import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import createContact from '@/shared/api/bookings/contacts/createContact';
import getTimezones from '@/shared/api/bookings/contacts/getTimezones';
import updateContact from '@/shared/api/bookings/contacts/updateContact';
import useModalDialog from '@/shared/hooks/useModalDialog';
import useNotificationBar from '@/shared/hooks/useNotificationBar';
import useRedirectLogin from '@/shared/hooks/useRedirectLogin';
import BookingsContact from '@/shared/types/bookings/contact.type';
import DialogName from '@/shared/types/dialogName.enum';

type OnUpdateDialogState = ReturnType<
  typeof useModalDialog<BookingsContact, DialogName>
>['onUpdateDialogState'];

type OnUpdateNotificationBar = ReturnType<
  typeof useNotificationBar
>['onUpdateNotificationBar'];

type Dialog = ReturnType<
  typeof useModalDialog<BookingsContact, DialogName>
>['dialog'];

type Args = {
  dialog: Dialog;
  onUpdateDialogState: OnUpdateDialogState;
  onUpdateNotificationBar: OnUpdateNotificationBar;
};

const useUserSettingsOperations = ({
  dialog,
  onUpdateDialogState,
  onUpdateNotificationBar,
}: Args) => {
  const { t } = useTranslation(['userSettings', 'common']);
  const redirectLogin = useRedirectLogin();

  const [refreshTableIndex, setRefreshTableIndex] = useState(0);

  const [timezones, setTimezones] = useState<string[]>([]);

  const handleCreateContact = async (formValues: Record<string, unknown>) => {
    const response = await createContact({
      body: formValues,
      onUnauthorized: redirectLogin,
    });

    if (response.success) {
      const newWindow = window.open(
        response.redirect_url,
        '_blank',
        'width=600,height=800',
      );

      if (newWindow) {
        const checkWindowClosed = setInterval(() => {
          if (newWindow.closed) {
            clearInterval(checkWindowClosed);

            onUpdateNotificationBar({
              open: true,
              message: t('userSettings:contactDetailsWereSuccessfullyCreated'),
              severity: 'success',
            });
            onUpdateDialogState(DialogName.ManageContact, {
              open: false,
              errors: [],
            });
            setRefreshTableIndex((prev) => prev + 1);
          }
        }, 1000);
      }
    } else {
      onUpdateDialogState(DialogName.ManageContact, {
        open: true,
        errors: [response.msg],
      });
    }
  };

  const handleReauthorizeAccount = async (
    formValues: Partial<BookingsContact>,
    contactId: string,
  ) => {
    const response = await updateContact({
      body: formValues,
      id: contactId,
      onUnauthorized: redirectLogin,
    });

    if (response.success) {
      const newWindow = window.open(
        response.redirect_url,
        '_blank',
        'width=600,height=800',
      );

      if (newWindow) {
        const checkWindowClosed = setInterval(() => {
          if (newWindow.closed) {
            clearInterval(checkWindowClosed);

            onUpdateNotificationBar({
              open: true,
              message: t('userSettings:accountWasSuccessfullyReauthorized'),
              severity: 'success',
            });
            setRefreshTableIndex((prev) => prev + 1);
          }
        }, 1000);
      }
    } else {
      onUpdateDialogState(DialogName.ManageContact, {
        open: true,
        errors: [response.msg],
      });
    }
  };

  const handleUpdateContact = async (formValues: Partial<BookingsContact>) => {
    const response = await updateContact({
      body: formValues,
      id: dialog?.manageContact?.data?.booking_contact_id || '',
      onUnauthorized: redirectLogin,
    });

    if (response.success) {
      onUpdateNotificationBar({
        open: true,
        message: t('userSettings:contactDetailsWereSuccessfullyUpdated'),
        severity: 'success',
      });
      onUpdateDialogState(DialogName.ManageContact, {
        open: false,
        errors: [],
      });
      setRefreshTableIndex((prev) => prev + 1);
    } else {
      onUpdateDialogState(DialogName.ManageContact, {
        open: true,
        errors: [response.msg],
      });
    }
  };

  useEffect(() => {
    const fetchTimezones = async () => {
      const response = await getTimezones(() => redirectLogin());

      if (response.success) {
        setTimezones(response.data);
      }
    };

    void fetchTimezones();
  }, [redirectLogin]);

  return {
    timezones,
    refreshTableIndex,
    handleCreateContact,
    handleUpdateContact,
    handleReauthorizeAccount,
  };
};

export default useUserSettingsOperations;
